// Delivery cutoff clock (blocks/delivery-estimate.liquid).
// Counts down to today's dispatch cutoff and names the day the order ships.
//
// Runs entirely on the client: the block renders the span empty and hidden, so a
// full-page-cached storefront has no countdown to serve stale, and a visitor
// without JS keeps the plain delivery estimate the block always showed.
//
// The cutoff belongs to the SHOP's wall clock, not the visitor's. Only the shop's
// UTC offset comes from the server (data-tz, minutes); "now" is the visitor's own
// UTC epoch shifted by it, then read with getUTC* so the getters return shop-local
// fields. That keeps the maths right for a visitor in any timezone.
// ponytail: the offset is baked at render time. A shop that observes DST reads an
// hour off on a page cached across the changeover — swap in Intl.DateTimeFormat
// with a real timeZone if one ever sells here.
(() => {
  const lang = document.documentElement.lang || undefined;
  const weekday = new Intl.DateTimeFormat(lang, { weekday: 'long', timeZone: 'UTC' });
  const pad = (n) => (n < 10 ? '0' + n : String(n));

  // Shared by the countdown clock AND the [data-ship-day] badge painter below: "how
  // many days from now does this dispatch, and what is that day called". Split out so
  // a badge elsewhere on the page (blocks/badge.liquid's [ship_day] placeholder) can
  // ask the same question without its own copy of the weekday/closed-day maths.
  const shipDayFor = (el) => {
    // Sunday-first, matching getUTCDay(). -1 = no dispatch that day.
    const cutoffs = el.dataset.cutoffs.split(',').map(Number);
    const now = new Date(Date.now() + Number(el.dataset.tz) * 60000);
    const today = now.getUTCDay();
    const mins = now.getUTCHours() * 60 + now.getUTCMinutes();

    // Days until dispatch: 0 while today is still open, otherwise the next day
    // that ships at all. 7 lands back on today next week, which is correct for a
    // shop that only ships one day a week.
    let ahead = cutoffs[today] >= 0 && mins < cutoffs[today] ? 0 : 1;
    while (ahead > 0 && ahead <= 7 && cutoffs[(today + ahead) % 7] < 0) ahead += 1;
    if (ahead > 7) return null;

    const target = (today + ahead) % 7;
    const dayWord =
      ahead === 0
        ? el.dataset.today
        : ahead === 1
          ? el.dataset.tomorrow
          : weekday.format(new Date(now.getTime() + ahead * 86400000));

    return { cutoffs, now, today, mins, ahead, target, dayWord };
  };

  // The countdown value and the day word each take their own colour, so the line is
  // built from NODES rather than one string. Splitting the TRANSLATED template on its
  // %X% tokens is what keeps word order intact: the spans land wherever the merchant's
  // language puts the placeholders, with no assumption that the number comes first.
  const fill = (el, tpl, parts) => {
    el.textContent = '';
    tpl.split(/(%[HMCD]%)/).forEach((chunk) => {
      const value = parts[chunk];
      if (value === undefined) {
        if (chunk) el.appendChild(document.createTextNode(chunk));
        return;
      }
      const span = document.createElement('span');
      span.className = chunk === '%D%' ? 'delivery-estimate__day' : 'delivery-estimate__count';
      span.textContent = value;
      // Reserve the value's width so the line stops breathing every tick: tabular-nums
      // only helps in a face that ships tnum, and several store fonts do not. The CSS
      // stacks an invisible copy with every digit swapped for "0" (the widest digit in
      // most faces) under the value. A `length`-in-ch reservation counted each colon of
      // "49:31:31" as a full digit and indented the line by the difference.
      if (chunk !== '%D%') span.dataset.ghost = value.replace(/\d/g, '0');
      el.appendChild(span);
    });
  };

  const paint = (el) => {
    const day = shipDayFor(el);
    if (!day) return;
    const { cutoffs, now, mins, ahead, target, dayWord } = day;

    if (ahead === 0 || el.dataset.after === 'countdown') {
      // Seconds, not whole minutes: the clock format ticks, and a minute-resolution
      // figure would sit frozen for up to 59 of every 60 ticks. The worded format reads
      // the same numbers out of it, so both stay in step.
      const left = (ahead * 1440 + cutoffs[target]) * 60 - (mins * 60 + now.getUTCSeconds());
      const h = Math.floor(left / 3600);
      const m = Math.floor((left % 3600) / 60);
      if (el.dataset.format === 'clock') {
        fill(el, el.dataset.tplClock, {
          '%C%': pad(h) + ':' + pad(m) + ':' + pad(left % 60),
          '%D%': dayWord,
        });
      } else {
        fill(el, h > 0 ? el.dataset.tplLong : el.dataset.tplShort, {
          '%H%': String(h),
          '%M%': String(m),
          '%D%': dayWord,
        });
      }
    } else {
      fill(el, el.dataset.tplNext, { '%D%': dayWord });
    }

    el.hidden = false;
    // Cutoff-only mode ships the whole row hidden, because nothing here renders
    // server-side and a JS-off visitor would otherwise get a lone icon.
    el.closest('[hidden]')?.removeAttribute('hidden');
  };

  // blocks/badge.liquid's [ship_day] placeholder ("Ships [ship_day]"): there is no
  // page-wide "which day ships" value in Liquid, so the badge ships hidden with the
  // label template in data-ship-day and this fills it from the FIRST cutoff clock on
  // the page. No clock on the page (estimate block off/absent) means these stay
  // hidden for good — fail-soft, nothing else to wire up.
  const paintShipDay = () => {
    const clock = all()[0];
    if (!clock) return;
    const day = shipDayFor(clock);
    if (!day) return;
    document.querySelectorAll('[data-ship-day]').forEach((el) => {
      const tpl = el.dataset.shipDay;
      if (!tpl) return;
      const at = tpl.indexOf('[ship_day]');
      if (at < 0) return;
      // Capitalise only when the token OPENS the label ("[ship_day] kargoda" ->
      // "Yarın kargoda"); mid-sentence ("Ships [ship_day]" -> "Ships tomorrow")
      // stays lower-case. Locale-aware casing, not a hardcoded .slice/.toUpperCase.
      let word = day.dayWord;
      if (at === 0) word = word.charAt(0).toLocaleUpperCase(lang) + word.slice(1);
      // Only the text span: the badge's icon, if it has one, sits beside it.
      (el.querySelector('[data-ship-day-text]') || el).textContent = tpl.replace('[ship_day]', word);
      el.hidden = false;
    });
  };

  const all = () => document.querySelectorAll('[data-cutoff-clock]');
  const paintAll = () => {
    all().forEach(paint);
    paintShipDay();
  };
  paintAll();

  // A second delivery-estimate block on the page re-runs this file, and the theme
  // editor re-runs it on every section reload — repainting is idempotent, adding a
  // second timer is not.
  if (window.deliveryCutoffTick) return;
  // A ticking HH:MM:SS clock has to repaint every second; the worded countdown only
  // changes once a minute, so it keeps the cheap 30s beat rather than paying for
  // precision it never shows.
  const ticks = Array.prototype.some.call(all(), (el) => el.dataset.format === 'clock');
  window.deliveryCutoffTick = setInterval(paintAll, ticks ? 1000 : 30000);
  document.addEventListener('shopify:section:load', paintAll);
})();
